"use client";

import Link from "next/link";
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";

export interface FlyoutItem {
  key: string;
  label: string;
  href: string;
  icon?: ReactNode;
  active: boolean;
  /** render a hairline divider above this item */
  dividerBefore?: boolean;
  /** dim the item (e.g. a dashboard view with no data yet); still navigable */
  disabled?: boolean;
  /** when > 0, a count pill renders on the item (e.g. pending approvals) */
  badgeCount?: number;
}

/* ------------------------------------------------------------------ */
/*  Hover coordinator                                                 */
/* ------------------------------------------------------------------ */

/** Pause before a hover opens a panel, so crossing the rail opens nothing. */
const OPEN_DELAY_MS = 140;
/** Grace period after leaving, so the pointer can travel into the panel. */
const CLOSE_DELAY_MS = 220;

type OpenSource = "hover" | "press";

interface NavFlyoutHostValue {
  openKey: string | null;
  openSource: OpenSource;
  hoverOpen: (key: string) => void;
  hoverLeave: () => void;
  cancelClose: () => void;
  press: (key: string) => void;
  close: () => void;
}

const NavFlyoutHostContext = createContext<NavFlyoutHostValue | null>(null);

/**
 * Coordinates the sidebar's hover panels so only one is ever open and moving
 * along the rail swaps panels instantly instead of opening a second one.
 *
 * Mount once around the nav. Timers live here (not per flyout) because the
 * open/close decision depends on whether a *sibling* is already open.
 */
export function NavFlyoutHost({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ key: string | null; source: OpenSource }>({
    key: null,
    source: "hover",
  });
  // Mirrors state.key so the callbacks below can branch on it without
  // re-creating themselves (and re-rendering every trigger) on each change.
  const openKeyRef = useRef<string | null>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);

  const clearOpen = useCallback(() => {
    if (openTimer.current !== undefined) {
      window.clearTimeout(openTimer.current);
      openTimer.current = undefined;
    }
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== undefined) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  }, []);

  useEffect(() => () => {
    clearOpen();
    cancelClose();
  }, [clearOpen, cancelClose]);

  const apply = useCallback((key: string | null, source: OpenSource) => {
    openKeyRef.current = key;
    setState({ key, source });
  }, []);

  const hoverOpen = useCallback(
    (key: string) => {
      clearOpen();
      cancelClose();
      if (openKeyRef.current === key) return;
      // Something is already open: swap with no delay so travelling the rail
      // reads as one continuous panel rather than a series of flickers.
      if (openKeyRef.current !== null) {
        apply(key, "hover");
        return;
      }
      openTimer.current = window.setTimeout(() => {
        openTimer.current = undefined;
        apply(key, "hover");
      }, OPEN_DELAY_MS);
    },
    [apply, cancelClose, clearOpen],
  );

  const hoverLeave = useCallback(() => {
    clearOpen();
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = undefined;
      apply(null, "hover");
    }, CLOSE_DELAY_MS);
  }, [apply, cancelClose, clearOpen]);

  const press = useCallback(
    (key: string) => {
      clearOpen();
      cancelClose();
      apply(openKeyRef.current === key ? null : key, "press");
    },
    [apply, cancelClose, clearOpen],
  );

  const close = useCallback(() => {
    clearOpen();
    cancelClose();
    apply(null, "hover");
  }, [apply, cancelClose, clearOpen]);

  return (
    <NavFlyoutHostContext.Provider
      value={{
        openKey: state.key,
        openSource: state.source,
        hoverOpen,
        hoverLeave,
        cancelClose,
        press,
        close,
      }}
    >
      {children}
    </NavFlyoutHostContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/*  Flyout                                                            */
/* ------------------------------------------------------------------ */

interface NavFlyoutProps {
  /** unique within the rail; identifies this panel to the host */
  navKey: string;
  label: string;
  icon?: ReactNode;
  /** empty for a plain link — hovering the collapsed rail then shows a label card */
  items: FlyoutItem[];
  /**
   * When set, the trigger is a link and a click navigates here. For a plain
   * link (no items) that is the destination itself; for a group it is the
   * group's default page, and the flyout still opens on hover, on ArrowDown/
   * ArrowRight for keyboard users, and on a first tap for touch users.
   */
  href?: string;
  collapsed: boolean;
  /** true when the current page is this link, or one of this group's items */
  containsActive: boolean;
  /** sidebar trigger styling (collapsed-aware), passed from sidebar-nav */
  triggerBase: string;
  triggerHighlight: string;
  /** leading active dot node, passed from sidebar-nav */
  dot: ReactNode;
  /** when set, the panel header shows "{count} {headerCountLabel}" beside the title */
  headerCountLabel?: string;
  /** when > 0, a count pill renders on the trigger (e.g. pending approvals) */
  badgeCount?: number;
  /** data-tour anchor for the Guided Tours runtime */
  tourAnchor?: string;
}

// Below this viewport width a side panel has no room — fall back to inline accordion.
const SIDE_PANEL_MIN_WIDTH = 768;
/** Keep the panel this far from the viewport edges when clamping. */
const VIEWPORT_MARGIN = 12;

const panelClass =
  "max-h-[calc(100vh-24px)] overflow-y-auto rounded-lg border border-spark-hairline bg-spark-navigation p-1.5 shadow-[var(--shadow-card-hover)]";
const panelTitleClass = "px-2 pb-2 pt-1 text-[15px] font-bold leading-tight text-spark-navigation-strong";
const itemBase =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-spark-navigation-text hover:bg-spark-navigation-hover hover:text-spark-navigation-strong transition-colors";
const itemActive =
  "flex items-center gap-2 rounded-md bg-spark-navigation-active px-2 py-1.5 text-sm font-bold text-spark-navigation-active-text transition-colors";
const itemDisabled =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-spark-navigation-text hover:bg-spark-navigation-hover transition-colors";

// SSR renders this component too; useLayoutEffect would warn there.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function NavFlyout({
  navKey,
  label,
  icon,
  items,
  href,
  collapsed,
  containsActive,
  triggerBase,
  triggerHighlight,
  dot,
  headerCountLabel,
  badgeCount,
  tourAnchor,
}: NavFlyoutProps) {
  const host = useContext(NavFlyoutHostContext);
  if (!host) throw new Error("NavFlyout must be rendered inside a NavFlyoutHost");
  const { openKey, openSource, hoverOpen, hoverLeave, cancelClose, press, close } = host;

  const [sidePanel, setSidePanel] = useState(true);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Positioning runs twice: pass 0 places the panel from the trigger rect so it
  // mounts and can be measured, pass 1 clamps it against the viewport. Both
  // happen in layout effects, so the browser only ever paints the clamped one.
  const [pass, setPass] = useState(0);
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Pointer type of the press that produced the next click. Read via
  // pointerdown because not every browser dispatches click as a PointerEvent.
  const lastPointerType = useRef("");

  const isMenu = items.length > 0;
  // A plain link already shows its label when expanded; only the icon rail
  // needs the hover card.
  const showsPanel = isMenu || collapsed;
  const open = showsPanel && openKey === navKey;

  useIsomorphicLayoutEffect(() => {
    if (!open) {
      setPos(null);
      setPass(0);
      return;
    }
    const useSide = window.innerWidth >= SIDE_PANEL_MIN_WIDTH;
    setSidePanel(useSide);
    if (!useSide) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const height = panelRef.current?.offsetHeight ?? 0;
    const top = height
      ? Math.max(VIEWPORT_MARGIN, Math.min(rect.top, window.innerHeight - VIEWPORT_MARGIN - height))
      : rect.top;
    setPos({ top, left: rect.right + 8 });
    if (pass === 0) setPass(1);
  }, [open, pass]);

  // While open: outside-click closes, Esc closes + restores focus, reflow on scroll/resize.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: MouseEvent) {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    }
    function onReflow() {
      setPass(0);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, close]);

  // Move focus into the panel when it was opened deliberately. Hover must never
  // steal focus — the pointer is just passing through.
  useEffect(() => {
    if (!open || openSource !== "press" || !isMenu) return;
    if (sidePanel && pos === null) return; // side panel not positioned yet
    panelRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
  }, [open, openSource, isMenu, sidePanel, pos]);

  // A link trigger navigates on Enter, so keyboard users open the menu with
  // ArrowDown/ArrowRight instead (the "press" source then moves focus into it).
  function onTriggerKeyDown(e: ReactKeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowRight") return;
    e.preventDefault();
    press(navKey);
  }

  function onPanelKeyDown(e: ReactKeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const links = Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>("a") ?? []);
    if (!links.length) return;
    const idx = links.findIndex((l) => l === document.activeElement);
    const nextIdx =
      e.key === "ArrowDown"
        ? (idx + 1) % links.length
        : (idx - 1 + links.length) % links.length;
    links[nextIdx].focus();
  }

  // A tap fires pointerenter too; on touch the panel has to come from the click
  // handler instead, or the first tap would both open and navigate.
  const hoverProps = {
    onPointerEnter: (e: ReactPointerEvent) => {
      if (e.pointerType !== "touch") hoverOpen(navKey);
    },
    onPointerLeave: (e: ReactPointerEvent) => {
      if (e.pointerType !== "touch") hoverLeave();
    },
  };

  const panel = (
    <div
      ref={panelRef}
      // A menu is interactive; a label card is decoration over an aria-labelled
      // trigger, so it stays out of the accessibility tree.
      {...(isMenu
        ? { role: "menu" as const, "aria-label": label }
        : { "aria-hidden": true as const })}
      onKeyDown={isMenu ? onPanelKeyDown : undefined}
      onPointerEnter={cancelClose}
      onPointerLeave={hoverProps.onPointerLeave}
      className={`spark-navigation-surface ${panelClass} ${isMenu ? "min-w-[196px]" : "whitespace-nowrap"}`}
    >
      <p className={panelTitleClass}>
        {label}
        {isMenu && headerCountLabel && (
          <span className="ml-1.5 text-[11px] font-medium text-spark-navigation-text">
            {items.length} {headerCountLabel}
          </span>
        )}
      </p>
      {items.map((it) => (
        <Fragment key={it.key}>
          {it.dividerBefore && <div className="my-1.5 mx-1 border-t border-spark-hairline" />}
          <Link
            href={it.href}
            role="menuitem"
            aria-current={it.active ? "page" : undefined}
            title={it.disabled ? "No data yet — connect integrations to populate" : undefined}
            className={it.active ? itemActive : it.disabled ? itemDisabled : itemBase}
            onClick={close}
          >
            {it.icon}
            <span>{it.label}</span>
            {(it.badgeCount ?? 0) > 0 && (
              <span className="ml-auto rounded-full bg-spark-green px-1.5 py-px text-[11px] font-bold text-spark-ink tnum">
                {it.badgeCount}
              </span>
            )}
          </Link>
        </Fragment>
      ))}
    </div>
  );

  // Width/shape is entirely the caller's (collapsed rail squares vs full-width rows).
  const triggerClass = containsActive ? triggerHighlight : triggerBase;
  const triggerBody = (
    <>
      {containsActive && !collapsed && dot}
      {icon}
      {!collapsed && <span>{label}</span>}
      {!collapsed && (badgeCount ?? 0) > 0 && (
        <span className="ml-auto rounded-full bg-spark-green px-1.5 py-px text-[11px] font-bold text-spark-ink tnum">
          {badgeCount}
        </span>
      )}
      {!collapsed && isMenu && (
        <span className={`${(badgeCount ?? 0) > 0 ? "" : "ml-auto "}text-xs opacity-70`} aria-hidden>
          ▸
        </span>
      )}
      {/* Collapsed rail: the icon carries the badge as a corner dot, since
          there's no room for a count pill. */}
      {collapsed && (badgeCount ?? 0) > 0 && (
        <span
          aria-hidden
          className="absolute right-1 top-1 h-2 w-2 rounded-full bg-spark-green ring-2 ring-spark-black"
        />
      )}
    </>
  );

  const trigger = href ? (
    <Link
      ref={(el) => {
        triggerRef.current = el;
      }}
      href={href}
      aria-current={!isMenu && containsActive ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      {...(isMenu ? { "aria-haspopup": "menu" as const, "aria-expanded": open } : {})}
      data-tour={tourAnchor}
      className={`relative ${triggerClass}`}
      onPointerDown={(e: ReactPointerEvent) => {
        lastPointerType.current = e.pointerType;
      }}
      onClick={(e) => {
        // Touch has no hover, so the first tap opens the flyout instead of
        // navigating; once it's open, taps navigate (an item or the trigger).
        if (isMenu && !open && lastPointerType.current === "touch") {
          e.preventDefault();
          lastPointerType.current = "";
          press(navKey);
          return;
        }
        close();
      }}
      onKeyDown={isMenu ? onTriggerKeyDown : undefined}
      onFocus={collapsed ? () => hoverOpen(navKey) : undefined}
      onBlur={collapsed ? () => hoverLeave() : undefined}
      {...hoverProps}
    >
      {triggerBody}
    </Link>
  ) : (
    <button
      ref={(el) => {
        triggerRef.current = el;
      }}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={collapsed ? label : undefined}
      data-tour={tourAnchor}
      onClick={() => press(navKey)}
      className={`relative ${triggerClass}`}
      {...hoverProps}
    >
      {triggerBody}
    </button>
  );

  return (
    <>
      {trigger}

      {open && sidePanel && pos && typeof document !== "undefined" &&
        createPortal(
          <div style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 60 }}>
            {panel}
          </div>,
          document.body
        )}

      {open && !sidePanel && isMenu && <div className="mt-0.5 pl-3">{panel}</div>}
    </>
  );
}
